import type { DlpLookupModel } from 'services/DlpConfiguration/DlpConfigurationService';
import { excelCellText, MAX_DLP_IMPORT_BYTES, readExcelWorkbook, readImportFile, SpreadsheetReadError } from './dlpImportFile';
import { normalizeDlpLookupName as normalizeName } from './dlpLookupNames';

export const MAX_BRAND_IMPORT_BYTES = MAX_DLP_IMPORT_BYTES;
export const MAX_BRAND_IMPORT_NAMES = 10000;

export type BrandImportErrorCode = 'unsupportedFile' | 'fileTooLarge' | 'tooManyBrands' | 'emptyFile' | 'invalidFile' | 'invalidCell';
export class BrandImportError extends Error {
    constructor(public readonly code: BrandImportErrorCode) {
        super(code);
        this.name = 'BrandImportError';
    }
}

export interface BrandImportResult {
    brandIds: string[];
    unmatched: string[];
    ambiguous: string[];
    duplicateCount: number;
}

const HEADERS = new Set(['brand', 'brands', 'brand name', 'brand names', 'brand_name', 'nome brand', 'nomi brand']);

// One name per row (or per line within a cell); an optional first-column header is ignored.
export const cleanBrandNames = (values: string[]): string[] => {
    const names = values.flatMap(value => value.split(/\r\n|\n|\r/)).map(value => value.trim()).filter(Boolean);
    if (names.length && HEADERS.has(normalizeName(names[0]))) names.shift();
    if (names.length > MAX_BRAND_IMPORT_NAMES) throw new BrandImportError('tooManyBrands');
    if (!names.length) throw new BrandImportError('emptyFile');
    return names;
};

// Exact normalized lookup matching, never fuzzy matching or invented IDs.
export const matchImportedBrands = (names: string[], brands: DlpLookupModel[]): BrandImportResult => {
    const candidates = new Map<string, Set<string>>();
    brands.forEach(brand => {
        const key = normalizeName(brand.name);
        if (!candidates.has(key)) candidates.set(key, new Set());
        candidates.get(key)!.add(brand.id);
    });
    const seen = new Set<string>();
    const selected = new Set<string>();
    const result: BrandImportResult = { brandIds: [], unmatched: [], ambiguous: [], duplicateCount: 0 };
    names.forEach(name => {
        const key = normalizeName(name);
        if (!key) return;
        if (seen.has(key)) {
            result.duplicateCount++;
            return;
        }
        seen.add(key);
        const ids = candidates.get(key);
        if (!ids?.size) result.unmatched.push(name);
        else if (ids.size > 1) result.ambiguous.push(name);
        else selected.add(Array.from(ids)[0]);
    });
    result.brandIds = Array.from(selected);
    return result;
};

export const readBrandNamesFromFile = async (file: File): Promise<string[]> => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension !== 'xlsx' && extension !== 'txt') throw new BrandImportError('unsupportedFile');
    if (file.size > MAX_BRAND_IMPORT_BYTES) throw new BrandImportError('fileTooLarge');
    try {
        if (extension === 'txt') return cleanBrandNames([await readImportFile(file, true) as string]);
        const workbook = await readExcelWorkbook(file);
        const sheet = workbook.worksheets[0];
        if (!sheet) throw new BrandImportError('emptyFile');
        if (sheet.actualRowCount > MAX_BRAND_IMPORT_NAMES + 1) throw new BrandImportError('tooManyBrands');
        const values: string[] = [];
        sheet.eachRow(row => values.push(excelCellText(row.getCell(1).value)));
        return cleanBrandNames(values);
    } catch (error) {
        if (error instanceof SpreadsheetReadError) throw new BrandImportError(error.code === 'unsafeId' ? 'invalidCell' : error.code);
        throw error;
    }
};
